import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { TUser } from '@modweb5/interfaces/authentication.interface';
import { TSystemInfo } from '@modweb5/interfaces/serverResponse.interface';
import { TUserFunction, TChildModuleOpener, TModuleIdentifier, TConfigSystem, TDataWidthInfo, TRecordInModuleWasChanged } from '@modweb5/interfaces/modules.interface';
import { CommonService } from '@modweb5/sharedServices/commonService/common.service';
import { environment } from '@modweb5/environments/frontends/environment';
import { TLangOptions } from '@modweb5/interfaces/translation.interface';

@Injectable({
  providedIn: 'root'
})
export class ObservableService {

	private _userInfo =  new BehaviorSubject<TUser | null>(null);
	userInfo$ = this._userInfo.asObservable();

	private _systemInfo =  new BehaviorSubject<TSystemInfo | null>(null);
	systemInfo$ = this._systemInfo.asObservable();

	private _spinnerVisibility = new BehaviorSubject<boolean>(false);
	spinnerVisibility$ = this._spinnerVisibility.asObservable();

	private _detailWidthInfo = new BehaviorSubject<null | TDataWidthInfo>(null);
	detailWidthInfo$ = this._detailWidthInfo.asObservable();

	private _isDemo = new BehaviorSubject<boolean>(false);
	isDemo$ = this._isDemo.asObservable();

	private _actualConfigSystem = new BehaviorSubject<TConfigSystem | null>(null);
	actualConfigSystem$ = this._actualConfigSystem.asObservable();

	private _isE2ETest = new BehaviorSubject<boolean>(false);
	isE2ETest$ = this._isE2ETest.asObservable();

	private _defaultConfigSystem = new BehaviorSubject<TConfigSystem | null>(null);
	defaultConfigSystem$ = this._defaultConfigSystem.asObservable();

	private _openNewModule = new Subject<string>();
	openNewModule$ = this._openNewModule.asObservable();

	private _slotHasBecomeActive = new Subject<number>();
	slotHasBecomeActive$ = this._slotHasBecomeActive.asObservable();

	private _runFunction = new Subject<TUserFunction>();
	runFunction$ = this._runFunction.asObservable();

	private _lang = new BehaviorSubject<string>('cs');
	lang$ = this._lang.asObservable();

	private _langOptions = new BehaviorSubject<TLangOptions | null>(null);
	langOptions$ = this._langOptions.asObservable();

	// Předěláno na BehaviorSubject, aby child modul, který v době otvírání není aktivní 
	// obdržel informaci, že byl zavolán a zavolal funkci checkViewportSize pro obnovu svého viewPortu.
	private _openChildModule = new BehaviorSubject<TChildModuleOpener>(null);
	openChildModule$ = this._openChildModule.asObservable();

	private _click = new Subject<any>();
	click$ = this._click.asObservable();

	private _keydown = new Subject<any>();
	keydown$ = this._keydown.asObservable();

	private _windowResize = new BehaviorSubject<any | null>(null);
	windowResize$ = this._windowResize.asObservable();

	private _changeTableWidthStart = new BehaviorSubject<boolean | null>(null);
	changeTableWidthStart$ = this._changeTableWidthStart.asObservable();

	private _changeTableWidthEnd = new BehaviorSubject<boolean | null>(null);
	changeTableWidthEnd$ = this._changeTableWidthEnd.asObservable();

	private _dataForModuleWasChanged = new Subject<TModuleIdentifier>();
	dataForModuleWasChanged$ = this._dataForModuleWasChanged.asObservable();

	private _recordInModuleWasChanged = new Subject<TRecordInModuleWasChanged>();
	recordInModuleWasChanged$ = this._recordInModuleWasChanged.asObservable();

	actualModuleInfo: TModuleIdentifier | null = null;

  constructor(
		private commonService: CommonService
	) {
	}

	// ----------------------------------------------------------------------------------------------
	emitUserInfo(aUserInfo: TUser): void {
		this._userInfo.next(aUserInfo);
	}

	// ----------------------------------------------------------------------------------------------
	emitLang(aLang: string): void {
		this.commonService.setCookie(`${environment.cookiePrefix}_lang`, aLang, 365);
		this._lang.next(aLang);
	}

	// ----------------------------------------------------------------------------------------------
	emitLangOptions(aLangOptions: TLangOptions | null): void {
		this._langOptions.next(aLangOptions);
	}

	// ----------------------------------------------------------------------------------------------
	emitSystemInfo(aSystemInfo: TSystemInfo): void {
		this._systemInfo.next(aSystemInfo);
	}

	// ----------------------------------------------------------------------------------------------
	emitKeydown(event) {
		this._keydown.next({ event, actualModuleInfo: this.actualModuleInfo });
	}

	// ----------------------------------------------------------------------------------------------
	emitClick(event) {
		this._click.next({ event, actualModuleInfo: this.actualModuleInfo });
	}

	// ----------------------------------------------------------------------------------------------
	emitWindowResize(event) {
		this._windowResize.next({ event, actualModuleInfo: this.actualModuleInfo });
	}

	// ----------------------------------------------------------------------------------------------
	emitChangeTableWidthStart() {
		this._changeTableWidthStart.next(true);
	}

	// ----------------------------------------------------------------------------------------------
	emitChangeTableWidthEnd() {
		this._changeTableWidthEnd.next(true);
	}

	// ----------------------------------------------------------------------------------------------
	setActualModuleInfo(aActualModuleInfo: TModuleIdentifier | null): void {
		this.actualModuleInfo = aActualModuleInfo;
	}

	// ----------------------------------------------------------------------------------------------
	emitSlotHasBecomeActive(aSlotId: number): void  {
		this._slotHasBecomeActive.next(aSlotId);
	}


	// ----------------------------------------------------------------------------------------------
	setSpinnerVisibility(aVisibility: boolean): void {
		this._spinnerVisibility.next(aVisibility);
	}

	// ----------------------------------------------------------------------------------------------
	emitIsDemo(aIsDemo: boolean): void {
		this._isDemo.next(aIsDemo);
	}

	// ----------------------------------------------------------------------------------------------
	emitDetailWidthInfo(detailWidthInfo: TDataWidthInfo): void {
		this._detailWidthInfo.next(detailWidthInfo);
	}

	// ----------------------------------------------------------------------------------------------
	openNewModule(aModuleId: string): void {
		this._openNewModule.next(aModuleId);
	}

	// ----------------------------------------------------------------------------------------------
	emitRunUserFunction(aInfo: TUserFunction): void {
		this._runFunction.next(aInfo);
	}

	// ----------------------------------------------------------------------------------------------
	emitOpenChildModule(aOpeningModuleInfo: TChildModuleOpener | null): void {
		this._openChildModule.next(aOpeningModuleInfo);
	}

	// ----------------------------------------------------------------------------------------------
	emitActualConfigSystem(aConfigSystem: TConfigSystem | null): void {

		this._actualConfigSystem.next(aConfigSystem);
	}

	// ----------------------------------------------------------------------------------------------
	emitIsE2ETest(isE2ETest: boolean): void {
		this._isE2ETest.next(isE2ETest);
	}

	// ----------------------------------------------------------------------------------------------
	emitDefaultConfigSystem(aConfigSystem: TConfigSystem | null): void {

		this._defaultConfigSystem.next(aConfigSystem);
	}

	// ----------------------------------------------------------------------------------------------
	emitDataForModuleWasChanged(aActualModuleInfo: TModuleIdentifier): void {
		this._dataForModuleWasChanged.next(aActualModuleInfo);
	}

	emitRecordInModuleWasChanged(aParentRowInfo : TRecordInModuleWasChanged): void {
		this._recordInModuleWasChanged.next(aParentRowInfo);
	}

}
